import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axiosInstance from 'axiosInstance';
import baseUrl from 'Base_Url/baseUrl';
import moment from 'moment-timezone';
import { useToast } from '@chakra-ui/react';

// Subcomponents
import ClientNavbar from './ClientNavbar';
import TopFilterBar from './TopFilterBar';
import HeroGaugeChart from './HeroGaugeChart';
import KpiStrip from './KpiStrip';
import ComplianceStrip from './ComplianceStrip';
import ClientTabNav from './ClientTabNav';
import OverviewTab from './tabs/OverviewTab';
import ConsumptionTab from './tabs/ConsumptionTab';
import PumpTab from './tabs/PumpTab';
import ReportsTab from './tabs/ReportsTab';
import SubscriptionTab from './tabs/SubscriptionTab';
import AllMetersView from './AllMetersView';
import ClientProfileView from './ClientProfileView';
import UsersView from 'views/admin/users';
import LimitModal from './LimitModal';
import MeterPhotoModal from './MeterPhotoModal';

export default function ClientDashboard() {
  const toast = useToast();
  const Token = localStorage.getItem('token');
  const storedCompanyId = localStorage.getItem('companyId');
  const storedCompanyName = localStorage.getItem('companyName') || '';

  // Navigation & View state
  const [currentView, setCurrentView] = useState('dashboard');
  const [activeTab, setActiveTab] = useState('overview');

  // Filter states
  const [plants, setPlants] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState(storedCompanyId || '');
  const [plantName, setPlantName] = useState(storedCompanyName || 'Plant');
  const [meters, setMeters] = useState([]);
  const [selectedMeter, setSelectedMeter] = useState('');
  const [unit, setUnit] = useState('m³');

  // Dashboard & Tab Date range states (default last 14 days)
  const [fromDate, setFromDate] = useState(moment().subtract(13, 'days').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));

  // Hero Chart isolated state & datasets (Daily vs Monthly affects only this chart)
  const [heroFromDate, setHeroFromDate] = useState(moment().subtract(13, 'days').format('YYYY-MM-DD'));
  const [heroToDate, setHeroToDate] = useState(moment().format('YYYY-MM-DD'));
  const [heroAggregation, setHeroAggregation] = useState('Daily');
  const [heroGraphSeries, setHeroGraphSeries] = useState([]);

  // Real Telemetry & metrics state
  const [todayConsumption, setTodayConsumption] = useState(0);
  const [todayDelta, setTodayDelta] = useState(null);
  const [monthConsumption, setMonthConsumption] = useState(0);
  const [flowRate, setFlowRate] = useState(0);
  const [flowDuration, setFlowDuration] = useState('');
  const [totalizer, setTotalizer] = useState(0);
  const [forwardTotalizer, setForwardTotalizer] = useState(0);
  const [reverseTotalizer, setReverseTotalizer] = useState(0);
  const [cumulativePumpHours, setCumulativePumpHours] = useState(0);
  const [todayPumpHours, setTodayPumpHours] = useState(0);
  const [internalLimit, setInternalLimit] = useState(0);
  const [complianceLimit, setComplianceLimit] = useState(0);
  const [deviceDetails, setDeviceDetails] = useState({});

  // Limit Modal State
  const [limitModalState, setLimitModalState] = useState({
    isOpen: false,
    type: 'compliance', // 'compliance' | 'internal' | 'alert'
    targetMeter: '',
    existingLimit: 0,
    existingNocDetails: {},
    existingAlertLimit: 0,
    existingAlertEmail: '',
    existingAlertFrequency: 'daily',
  });

  // Meter Photo Modal State
  const [meterPhotoModalState, setMeterPhotoModalState] = useState({
    isOpen: false,
    targetMeter: '',
  });

  // Real API datasets
  const [dailyConsumptionLogs, setDailyConsumptionLogs] = useState([]);
  const [maxDailyExtraction, setMaxDailyExtraction] = useState(0);
  const [avgDailyExtraction, setAvgDailyExtraction] = useState(0);
  const [pumpMetrics, setPumpMetrics] = useState({
    totalHours: '0 hrs 0 mins',
    cycles: '0 days',
    yesterdayRunning: '0 hrs 0 mins',
    monthRunning: '0 hrs 0 mins',
  });
  const [pumpRecordsList, setPumpRecordsList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dynamic Compliance Breaches Count
  const calculatedBreachesCount = useMemo(() => {
    if (!complianceLimit || complianceLimit <= 0 || !dailyConsumptionLogs || dailyConsumptionLogs.length === 0) {
      return 0;
    }
    return dailyConsumptionLogs.filter((item) => Number(item.v) > complianceLimit).length;
  }, [complianceLimit, dailyConsumptionLogs]);

  // 1. Fetch Real Companies / Plants from Backend
  const fetchCompanies = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`${baseUrl}/company/list`, {
        headers: { Authorization: `Bearer ${Token}` },
      });
      const list = res?.data?.data?.data || res?.data?.data || [];
      if (Array.isArray(list) && list.length > 0) {
        const plantOptions = list.map((c) => ({
          label: c.name || 'Plant',
          value: c._id || c.id || c.companyId,
          allowedLimit: c.allowedLimit || 0,
        }));
        setPlants(plantOptions);

        const current =
          plantOptions.find((p) => p.value === selectedPlant || p.value === storedCompanyId) ||
          plantOptions[0];
        setSelectedPlant(current.value);
        setPlantName(current.label);
      } else if (storedCompanyId || storedCompanyName) {
        const fallback = [
          {
            label: storedCompanyName || 'Primary Plant',
            value: storedCompanyId || 'default',
            allowedLimit: 0,
          },
        ];
        setPlants(fallback);
        setSelectedPlant(fallback[0].value);
        setPlantName(fallback[0].label);
      }
    } catch (err) {
      console.warn('Company list fetch error:', err);
      if (storedCompanyName || storedCompanyId) {
        setPlants([
          {
            label: storedCompanyName || 'Primary Plant',
            value: storedCompanyId || 'default',
            allowedLimit: 0,
          },
        ]);
      }
    }
  }, [Token, selectedPlant, storedCompanyId, storedCompanyName]);

  // 2. Fetch Real Devices / Meters from Backend
  const fetchMeters = useCallback(async () => {
    try {
      let meterOptions = [];

      // Try /device/device-list first
      const res = await axiosInstance
        .get(`${baseUrl}/device/device-list`, {
          headers: { Authorization: `Bearer ${Token}` },
        })
        .catch(() => null);

      const deviceList = res?.data?.data?.data || res?.data?.data;
      if (Array.isArray(deviceList) && deviceList.length > 0) {
        meterOptions = deviceList.map((d) => ({
          _id: d._id || d.id,
          id: d._id || d.id,
          label: `${d.productId} · ${d.productName || 'Borewell'}`,
          value: d.productId,
          productId: d.productId,
          flowmeterSerialNumber: d.flowmeterSerialNumber || '',
          name: d.productName || 'Borewell',
          productName: d.productName || 'Borewell',
          internalLimit: d.internalLimit !== undefined && d.internalLimit !== null ? Number(d.internalLimit) : 0,
          complianceLimit: d.complianceLimit !== undefined && d.complianceLimit !== null ? Number(d.complianceLimit) : 0,
          alertLimit: d.alertLimit !== undefined && d.alertLimit !== null ? Number(d.alertLimit) : 0,
          alertEmail: d.alertEmail || '',
          alertFrequency: d.alertFrequency || 'daily',
          calibrationCertificate: d.calibrationCertificate,
          latitude: d.latitude !== undefined && d.latitude !== null && !isNaN(Number(d.latitude)) ? Number(d.latitude) : 28.541505,
          longitude: d.longitude !== undefined && d.longitude !== null && !isNaN(Number(d.longitude)) ? Number(d.longitude) : 77.332897,
          meterPhoto: d.meterPhoto,
          nocDetails: d.nocDetails || {},
        }));
      } else {
        // Fallback to /device-readings/unique
        const filter = selectedPlant ? { companyId: selectedPlant } : {};
        const queryStr =
          Object.keys(filter).length > 0
            ? `?filter=${encodeURIComponent(JSON.stringify(filter))}`
            : '';
        const uniqueRes = await axiosInstance
          .get(`${baseUrl}/device-readings/unique${queryStr}`, {
            headers: { Authorization: `Bearer ${Token}` },
          })
          .catch(() => null);

        const uniqueList = uniqueRes?.data?.data?.data || uniqueRes?.data?.data;
        if (Array.isArray(uniqueList) && uniqueList.length > 0) {
          meterOptions = uniqueList.map((pid) => ({
            label: `${pid} · Borewell`,
            value: pid,
            productId: pid,
            name: 'Borewell',
            productName: 'Borewell',
          }));
        }
      }

      if (meterOptions.length > 0) {
        setMeters(meterOptions);
        if (!selectedMeter || !meterOptions.some((m) => m.value === selectedMeter)) {
          setSelectedMeter(meterOptions[0].value);
        }
      }
    } catch (err) {
      console.warn('Meters fetch error:', err);
    }
  }, [Token, selectedPlant, selectedMeter]);

  // 3. Fetch Real Device Details & Specifications from Backend (/device)
  const fetchDeviceSpecs = useCallback(async () => {
    if (!selectedMeter || selectedMeter === 'All') return;
    try {
      const filter = { productId: selectedMeter };
      const res = await axiosInstance
        .get(`${baseUrl}/device?filter=${encodeURIComponent(JSON.stringify(filter))}`, {
          headers: { Authorization: `Bearer ${Token}` },
        })
        .catch(() => null);

      const deviceList = res?.data?.data?.data || res?.data?.data;
      if (Array.isArray(deviceList) && deviceList.length > 0) {
        const found = deviceList[0];

        const savedKey = `limits_${selectedMeter}`;
        const localLimits = JSON.parse(localStorage.getItem(savedKey) || '{}');
        const devInternal =
          found.internalLimit !== undefined && found.internalLimit !== null
            ? Number(found.internalLimit)
            : localLimits.internalLimit !== undefined
            ? Number(localLimits.internalLimit)
            : 0;
        const devCompliance =
          found.complianceLimit !== undefined && found.complianceLimit !== null
            ? Number(found.complianceLimit)
            : localLimits.complianceLimit !== undefined
            ? Number(localLimits.complianceLimit)
            : 0;

        const devAlertLimit =
          found.alertLimit !== undefined && found.alertLimit !== null
            ? Number(found.alertLimit)
            : localLimits.alertLimit !== undefined
            ? Number(localLimits.alertLimit)
            : 0;
        const devAlertEmail = found.alertEmail || localLimits.alertEmail || '';
        const devAlertFrequency = found.alertFrequency || localLimits.alertFrequency || 'daily';

        setInternalLimit(devInternal);
        setComplianceLimit(devCompliance);

        const devLat =
          found.latitude !== undefined && found.latitude !== null && !isNaN(Number(found.latitude)) && Number(found.latitude) !== 0
            ? Number(found.latitude)
            : 28.541505;
        const devLng =
          found.longitude !== undefined && found.longitude !== null && !isNaN(Number(found.longitude)) && Number(found.longitude) !== 0
            ? Number(found.longitude)
            : 77.332897;

        setDeviceDetails((prev) => ({
          ...prev,
          _id: found._id || found.id,
          id: found._id || found.id,
          productId: found.productId,
          productName: found.productName,
          model: found.model,
          make: found.make,
          diameter: found.diameter,
          flowmeterSerialNumber: found.flowmeterSerialNumber || '',
          dashboardLicensingExpiry: found.dashboardLicensingExpiry || found.dashboardValidity?.endDate,
          warrantyExpiry: found.flowmeterWarranty?.endDate,
          commissioningDate: found.commissioningDate || found.createdAt,
          createdAt: found.createdAt,
          active: found.active !== undefined ? found.active : true,
          calibrationCertificate: found.calibrationCertificate,
          meterPhoto: found.meterPhoto,
          nocDetails: found.nocDetails || localLimits.nocDetails || {},
          internalLimit: devInternal,
          complianceLimit: devCompliance,
          alertLimit: devAlertLimit,
          alertEmail: devAlertEmail,
          alertFrequency: devAlertFrequency,
          latitude: devLat,
          longitude: devLng,
          lat: devLat,
          lng: devLng,
          cgwaBorewellId: found.cgwaBorewellId || found.borewellId || '',
        }));
      }
    } catch (err) {
      console.warn('Device specs error:', err);
    }
  }, [selectedMeter, Token]);

  // 4. Fetch Real Latest Telemetry & Cumulative Consumption (/latest-readings & /latest)
  const fetchTelemetry = useCallback(async () => {
    if (!selectedMeter || selectedMeter === 'All') return;
    try {
      setLoading(true);

      const filter = { productId: selectedMeter };
      if (selectedPlant) filter.companyId = selectedPlant;
      const encodedFilter = encodeURIComponent(JSON.stringify(filter));

      // Fetch live readings & cumulative period totals in parallel
      const [latestRes, cumulativeRes] = await Promise.all([
        axiosInstance
          .get(`${baseUrl}/device-readings/latest-readings?filter=${encodedFilter}`, {
            headers: { Authorization: `Bearer ${Token}` },
          })
          .catch(() => null),
        axiosInstance
          .get(`${baseUrl}/device-readings/latest?filter=${encodedFilter}`, {
            headers: { Authorization: `Bearer ${Token}` },
          })
          .catch(() => null),
      ]);

      // 1. Process Live Instant Telemetry
      const items = latestRes?.data?.data?.data || latestRes?.data?.data;
      if (Array.isArray(items) && items.length > 0) {
        const item = items[0];
        const fr = Number((Number(item.flowrate) || 0).toFixed(2));
        setFlowRate(fr);
        setFlowDuration(fr > 0 ? 'Flow active' : 'Pump idle');

        const fwd = Number((Number(item.totalizer ?? item.forwardTotalizer ?? item.forward_totalizer) || 0).toFixed(2));
        const rev = Number((Number(item.reverseTotalizer ?? item.reverse_totalizer ?? item.reverse) || 0).toFixed(2));
        const net = Number((fwd - rev).toFixed(2));

        setForwardTotalizer(fwd);
        setReverseTotalizer(rev);
        setTotalizer(net);

        const formatReadingTimestamp = (timestamp) => {
          if (!timestamp) return 'Recently';
          const istOffset = 5.5 * 60 * 60 * 1000;
          const adjustedDate = new Date(new Date(timestamp).getTime() - istOffset);
          return moment(adjustedDate).format('DD/MM/YYYY HH:mm');
        };

        setDeviceDetails((prev) => ({
          ...prev,
          productId: item.productId || selectedMeter,
          productName: item.productName || prev.productName || 'Borewell',
          lastCommunicated: formatReadingTimestamp(item.timestamp),
          dashboardLicensingExpiry: item.dashboardLicense || prev.dashboardLicensingExpiry,
          calibrationCertificate: item.calibrationCertificate,
          cgwaBorewellId: item.cgwaBorewellId || prev.cgwaBorewellId || '',
          batteryLevel: item.batteryLevel !== undefined ? item.batteryLevel : prev.batteryLevel,
          isRepairing: item.isRepairing !== undefined ? item.isRepairing : prev.isRepairing,
        }));
      }

      // 2. Process Computed Today & Monthly Consumption from /latest API
      const cumItems = cumulativeRes?.data?.data?.data || cumulativeRes?.data?.data;
      if (Array.isArray(cumItems) && cumItems.length > 0) {
        const cum = cumItems[0];
        if (cum.todayTotal !== undefined) {
          setTodayConsumption(Number((Number(cum.todayTotal) || 0).toFixed(2)));
        }
        if (cum.monthlyCumulative !== undefined) {
          setMonthConsumption(Number((Number(cum.monthlyCumulative) || 0).toFixed(2)));
        }
        if (cum.cumulativePumpHours !== undefined) {
          setCumulativePumpHours(Number((Number(cum.cumulativePumpHours) || 0).toFixed(2)));
        }
        if (cum.todayPumpHours !== undefined) {
          setTodayPumpHours(Number((Number(cum.todayPumpHours) || 0).toFixed(2)));
        }
      }
    } catch (err) {
      console.warn('Telemetry error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedMeter, selectedPlant, Token]);

  // 5a. Fetch Isolated Hero Bar Chart Data (/device-readings/graph)
  const fetchHeroGraph = useCallback(async () => {
    if (!selectedMeter || selectedMeter === 'All') return;
    try {
      const interval = heroAggregation === 'Monthly' ? '1mo' : '1d';
      const res = await axiosInstance
        .get(
          `${baseUrl}/device-readings/graph?productId=${selectedMeter}&startDate=${heroFromDate}&endDate=${heroToDate}&interval=${interval}&limit=500`,
          { headers: { Authorization: `Bearer ${Token}` } }
        )
        .catch(() => null);

      const items = res?.data?.data?.data || res?.data?.data;
      if (Array.isArray(items)) {
        const chronologicalItems = [...items].sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateA - dateB;
        });

        const mappedSeries = chronologicalItems.map((item) => {
          let label = item.date ? moment(item.date).format('D MMM') : item.key;
          if (heroAggregation === 'Monthly') {
            label = item.key || (item.date ? moment(item.date).format('MMM YYYY') : 'Month');
          }
          return {
            label,
            value: Number((Number(item.usage) || 0).toFixed(2)),
          };
        });

        setHeroGraphSeries(mappedSeries);
      }
    } catch (err) {
      console.warn('Hero graph fetch error:', err);
    }
  }, [selectedMeter, heroFromDate, heroToDate, heroAggregation, Token]);

  // 5b. Fetch Tab Daily Consumption Data & Stats (/device-readings/graph)
  const fetchDailyConsumptionData = useCallback(async () => {
    if (!selectedMeter || selectedMeter === 'All') return;
    try {
      const res = await axiosInstance
        .get(
          `${baseUrl}/device-readings/graph?productId=${selectedMeter}&startDate=${fromDate}&endDate=${toDate}&interval=1d&limit=500`,
          { headers: { Authorization: `Bearer ${Token}` } }
        )
        .catch(() => null);

      const items = res?.data?.data?.data || res?.data?.data;
      if (Array.isArray(items)) {
        const chronologicalItems = [...items].sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateA - dateB;
        });

        // Descending order for Daily logs table (Newest -> Oldest)
        const descendingItems = [...items].sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateB - dateA;
        });

        const logs = descendingItems.map((item) => {
          const usageVal = Number((Number(item.usage) || 0).toFixed(2));
          const logDate = item.date ? moment(item.date).format('YYYY-MM-DD') : fromDate;
          return {
            s: `${logDate} ${item.startTime && item.startTime !== '-' ? item.startTime : '00:00'}`,
            e: `${logDate} ${item.endTime && item.endTime !== '-' ? item.endTime : '23:59'}`,
            dur: usageVal > 0 ? 'Active' : 'No flow',
            avg: Number(item.usage ? (item.usage / 24).toFixed(2) : 0),
            v: usageVal,
            rev: 0.0,
            net: usageVal,
          };
        });
        setDailyConsumptionLogs(logs);

        // Compute statistics for Overview & KPI
        const values = chronologicalItems.map((item) => Number(item.usage) || 0);
        if (values.length > 0) {
          const positiveVals = values.filter((v) => v > 0);
          const maxV = positiveVals.length > 0 ? Math.max(...positiveVals) : 0;
          const avgV =
            positiveVals.length > 0
              ? positiveVals.reduce((a, b) => a + b, 0) / positiveVals.length
              : 0;
          setMaxDailyExtraction(Number(maxV.toFixed(2)));
          setAvgDailyExtraction(Number(avgV.toFixed(2)));

          // Calculate trend delta between the latest 2 days in chronological order
          if (values.length >= 2) {
            const last = values[values.length - 1];
            const prev = values[values.length - 2];
            if (prev > 0) {
              const delta = ((last - prev) / prev) * 100;
              setTodayDelta(Number(delta.toFixed(2)));
            } else {
              setTodayDelta(0);
            }
          }
        }
      }
    } catch (err) {
      console.warn('Daily consumption fetch error:', err);
    }
  }, [selectedMeter, fromDate, toDate, Token]);

  // 6. Fetch Real Motor / Pump Analytics (/device-readings/motor-analytics)
  const fetchMotorAnalytics = useCallback(async () => {
    if (!selectedMeter || selectedMeter === 'All') return;
    try {
      const res = await axiosInstance
        .get(
          `${baseUrl}/device-readings/motor-analytics?productId=${selectedMeter}&startDate=${fromDate}&endDate=${toDate}`,
          { headers: { Authorization: `Bearer ${Token}` } }
        )
        .catch(() => null);

      const daily = res?.data?.data?.dailyData;
      if (Array.isArray(daily)) {
        const totalHours = daily.reduce(
          (acc, d) => acc + (Number(d.motorOnHoursDecimal) || 0),
          0
        );
        const currentMonthKey = moment().format('YYYY-MM');
        const currentMonthHours = daily
          .filter((d) => (d.date || '').startsWith(currentMonthKey) || moment(d.date).isSame(moment(), 'month'))
          .reduce((acc, d) => acc + (Number(d.motorOnHoursDecimal) || 0), 0);

        const formatHoursToHuman = (decimalHours) => {
          if (isNaN(decimalHours) || decimalHours === null || decimalHours === undefined) return '0 hrs 0 mins';
          const totalMinutes = Math.round(Number(decimalHours) * 60);
          const hrs = Math.floor(totalMinutes / 60);
          const mins = totalMinutes % 60;
          return `${hrs} hrs ${mins} mins`;
        };

        const yesterdayItem = daily.length > 1 ? daily[daily.length - 2] : daily[0];

        setPumpMetrics({
          totalHours: formatHoursToHuman(totalHours),
          cycles: `${daily.filter((d) => (Number(d.motorOnHoursDecimal) || 0) > 0).length} days`,
          yesterdayRunning:
            yesterdayItem?.motorOnHoursDecimal !== undefined
              ? formatHoursToHuman(yesterdayItem.motorOnHoursDecimal)
              : (yesterdayItem?.motorOnHours || '0 hrs 0 mins'),
          monthRunning: formatHoursToHuman(currentMonthHours),
        });

        let cum = 0;
        const records = daily
          .map((d) => {
            const decimalVal = Number(d.motorOnHoursDecimal) || 0;
            cum += decimalVal;

            // 8-hour shift breakdown (Shift A: 12 AM–8 AM, Shift B: 8 AM–4 PM, Shift C: 4 PM–12 AM)
            const shiftA = d.shiftA_hours !== undefined ? d.shiftA_hours : Number((decimalVal * 0.33).toFixed(2));
            const shiftB = d.shiftB_hours !== undefined ? d.shiftB_hours : Number((decimalVal * 0.34).toFixed(2));
            const shiftC = d.shiftC_hours !== undefined ? d.shiftC_hours : Number((decimalVal * 0.33).toFixed(2));

            const avgFlowVal =
              d.avgFlowrateValue !== undefined
                ? Number(d.avgFlowrateValue)
                : (parseFloat(d.avgFlowrate) || 0);

            return {
              date: d.formattedDate || d.date,
              rawDate: d.date,
              cum: d.cumHours || formatHoursToHuman(cum),
              daily: d.motorOnHours || formatHoursToHuman(decimalVal),
              decimalHours: decimalVal,
              a: d.shiftA_formatted || formatHoursToHuman(shiftA),
              b: d.shiftB_formatted || formatHoursToHuman(shiftB),
              c: d.shiftC_formatted || formatHoursToHuman(shiftC),
              rawA: shiftA,
              rawB: shiftB,
              rawC: shiftC,
              cyc: d.cycles !== undefined ? d.cycles : (decimalVal > 0 ? 1 : 0),
              avgFlowrate: avgFlowVal.toFixed(2),
            };
          })
          .reverse();

        setPumpRecordsList(records);
      }
    } catch (err) {
      console.warn('Motor analytics fetch error:', err);
    }
  }, [selectedMeter, fromDate, toDate, Token]);

  // Lifecycle effects
  // 1. Initial Load: Fetch companies
  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // 2. When plant changes: Fetch meter list
  useEffect(() => {
    if (selectedPlant) {
      fetchMeters();
    }
  }, [selectedPlant, fetchMeters]);

  // 3. When selectedMeter or plant changes: Fetch Device Specs & Latest Telemetry (1 time per meter)
  useEffect(() => {
    if (selectedMeter && selectedMeter !== 'All') {
      fetchTelemetry();
      fetchDeviceSpecs();
    }
  }, [selectedMeter, selectedPlant, fetchTelemetry, fetchDeviceSpecs]);

  // 4a. When Hero Chart dates, aggregation, or meter changes: Fetch ONLY Hero Graph data
  useEffect(() => {
    if (selectedMeter && selectedMeter !== 'All') {
      fetchHeroGraph();
    }
  }, [selectedMeter, heroFromDate, heroToDate, heroAggregation, fetchHeroGraph]);

  // 4b. When tab dates or meter changes: Fetch Daily Consumption Data
  useEffect(() => {
    if (selectedMeter && selectedMeter !== 'All') {
      fetchDailyConsumptionData();
    }
  }, [selectedMeter, fromDate, toDate, fetchDailyConsumptionData]);

  // 5. When tab dates or meter changes: Fetch Motor Analytics (for both Pump and Consumption shift tracking)
  useEffect(() => {
    if (selectedMeter && selectedMeter !== 'All') {
      fetchMotorAnalytics();
    }
  }, [selectedMeter, fromDate, toDate, fetchMotorAnalytics]);

  // 6. Handle Hero Chart Aggregation Switch (Daily vs Monthly) — affects ONLY the hero bar chart
  const handleHeroAggregationChange = (mode) => {
    setHeroAggregation(mode);
    if (mode === 'Monthly') {
      const targetMoment = heroToDate ? moment(heroToDate) : moment();
      setHeroFromDate(targetMoment.clone().subtract(5, 'months').startOf('month').format('YYYY-MM-DD'));
      setHeroToDate(targetMoment.clone().endOf('month').format('YYYY-MM-DD'));
    } else {
      setHeroFromDate(moment().subtract(13, 'days').format('YYYY-MM-DD'));
      setHeroToDate(moment().format('YYYY-MM-DD'));
    }
  };

  // REAL FILE DOWNLOADS
  // 1. Hero Bar Chart Excel Export (Matches current Hero view)
  const handleDownloadHeroGraph = async (format = 'excel') => {
    if (!selectedMeter || selectedMeter === 'All') {
      toast({ title: 'Please select a specific meter first', status: 'warning' });
      return;
    }
    try {
      const activeInterval = heroAggregation === 'Monthly' ? '1mo' : '1d';
      const activeReportType = activeInterval === '1d' ? 'daily-consumption' : 'consumption';
      const intervalLabel = activeInterval === '1mo' ? 'Monthly_Usage' : 'Daily_Consumption';

      toast({ title: `Generating ${intervalLabel.replace(/_/g, ' ')} ${format.toUpperCase()} report...`, status: 'info' });
      const response = await axiosInstance.get(
        `${baseUrl}/device-readings/download-graph-data?productId=${selectedMeter}&startDate=${heroFromDate}&endDate=${heroToDate}&interval=${activeInterval}&format=${format}&reportType=${activeReportType}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${Token}` },
        }
      );
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const mime =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedMeter}_${intervalLabel}_Report_${heroFromDate}_to_${heroToDate}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: `${intervalLabel.replace(/_/g, ' ')} ${format.toUpperCase()} downloaded successfully`, status: 'success' });
    } catch (err) {
      toast({ title: 'Failed to download report', status: 'error' });
    }
  };

  // 2. Tab Water Usage Report Download (Excel or PDF for Consumption/Overview tabs)
  const handleDownloadGraph = async (format = 'excel', customInterval = null, reportType = null) => {
    if (!selectedMeter || selectedMeter === 'All') {
      toast({ title: 'Please select a specific meter first', status: 'warning' });
      return;
    }
    try {
      const activeInterval = customInterval || '1d';
      const activeReportType =
        reportType || (customInterval ? 'raw-telemetry' : activeInterval === '1d' ? 'daily-consumption' : 'consumption');
      const intervalLabel =
        activeReportType === 'daily-consumption'
          ? 'Daily_Consumption'
          : activeReportType === 'raw-telemetry'
          ? `Raw_Telemetry_${activeInterval}`
          : activeInterval === '1mo'
          ? 'Monthly_Usage'
          : `${activeInterval}_Usage`;

      toast({ title: `Generating ${intervalLabel.replace(/_/g, ' ')} ${format.toUpperCase()} report...`, status: 'info' });
      const response = await axiosInstance.get(
        `${baseUrl}/device-readings/download-graph-data?productId=${selectedMeter}&startDate=${fromDate}&endDate=${toDate}&interval=${activeInterval}&format=${format}&reportType=${activeReportType}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${Token}` },
        }
      );
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const mime =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedMeter}_${intervalLabel}_Report_${fromDate}_to_${toDate}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: `${intervalLabel.replace(/_/g, ' ')} ${format.toUpperCase()} downloaded successfully`, status: 'success' });
    } catch (err) {
      toast({ title: 'Failed to download report', status: 'error' });
    }
  };

  // 2. Pump Analytics Report Download (Excel or PDF)
  const handleDownloadPump = async (format = 'excel') => {
    if (!selectedMeter || selectedMeter === 'All') {
      toast({ title: 'Please select a specific meter first', status: 'warning' });
      return;
    }
    try {
      toast({ title: `Generating ${format.toUpperCase()} pump report...`, status: 'info' });
      const response = await axiosInstance.get(
        `${baseUrl}/device-readings/download-motor-analytics?productId=${selectedMeter}&startDate=${fromDate}&endDate=${toDate}&format=${format}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${Token}` },
        }
      );
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const mime =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedMeter}_Pump_Analytics_${fromDate}_to_${toDate}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: `Pump ${format.toUpperCase()} downloaded successfully`, status: 'success' });
    } catch (err) {
      toast({ title: 'Failed to download pump report', status: 'error' });
    }
  };

  const certInputRef = useRef(null);
  const [certTargetMeter, setCertTargetMeter] = useState(null);

  // 3. Calibration Certificate View (PDF in browser tab)
  const handleDownloadCert = async (meterId, originalFileName) => {
    const targetMeter = typeof meterId === 'string' && meterId ? meterId : selectedMeter;
    if (!targetMeter || targetMeter === 'All') {
      toast({ title: 'Please select a specific meter first', status: 'warning' });
      return;
    }
    try {
      toast({ title: `Opening calibration certificate for ${targetMeter}...`, status: 'info', duration: 1500 });
      const response = await axiosInstance.get(
        `${baseUrl}/device/download-certificate/${targetMeter}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${Token}` },
        }
      );
      if (response.data?.type === 'application/json') {
        const text = await response.data.text();
        const json = JSON.parse(text);
        throw new Error(json.message || json.data || 'Certificate file not available.');
      }
      const fileBlob = new Blob([response.data], { type: 'application/pdf' });
      const fileUrl = window.URL.createObjectURL(fileBlob);
      window.open(fileUrl, '_blank');
      toast({ title: 'Calibration certificate opened in browser tab', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: err.message || 'No certificate uploaded for this meter', status: 'warning' });
    }
  };

  // 3b. NOC View / Download (PDF in browser tab)
  const handleDownloadNoc = async (meterId, originalFileName) => {
    const targetMeter = typeof meterId === 'string' && meterId ? meterId : selectedMeter;
    if (!targetMeter || targetMeter === 'All') {
      toast({ title: 'Please select a specific meter first', status: 'warning' });
      return;
    }
    try {
      toast({ title: `Opening NOC for ${targetMeter}...`, status: 'info', duration: 1500 });
      const response = await axiosInstance.get(
        `${baseUrl}/device/download-noc/${targetMeter}`,
        {
          responseType: 'blob',
          headers: { Authorization: `Bearer ${Token}` },
        }
      );
      if (response.data?.type === 'application/json') {
        const text = await response.data.text();
        const json = JSON.parse(text);
        throw new Error(json.message || json.data || 'NOC document not available.');
      }
      const fileBlob = new Blob([response.data], { type: 'application/pdf' });
      const fileUrl = window.URL.createObjectURL(fileBlob);
      window.open(fileUrl, '_blank');
      toast({ title: 'NOC PDF opened in browser tab', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: err.message || 'No NOC document uploaded for this meter', status: 'warning' });
    }
  };

  // 4b. Meter Photo & GPS Coordinates Handlers
  const handleOpenMeterPhotoModal = (meterId) => {
    const target =
      typeof meterId === 'string' && meterId
        ? meterId
        : selectedMeter !== 'All'
        ? selectedMeter
        : meters[0]?.value || '';

    if (!target || target === 'All') {
      toast({ title: 'Please select a specific meter first', status: 'warning' });
      return;
    }

    setMeterPhotoModalState({
      isOpen: true,
      targetMeter: target,
    });
  };

  const handleSaveMeterPhoto = async ({ meterId, latitude, longitude, photoFile }) => {
    const target = meterId || selectedMeter;
    if (!target || target === 'All') {
      toast({ title: 'Please select a specific meter', status: 'warning' });
      return;
    }

    try {
      toast({ title: `Updating meter photo & GPS coordinates for ${target}...`, status: 'info' });
      const formData = new FormData();
      if (photoFile) {
        formData.append('photo', photoFile);
      }
      if (latitude !== undefined && latitude !== null) {
        formData.append('latitude', latitude);
      }
      if (longitude !== undefined && longitude !== null) {
        formData.append('longitude', longitude);
      }

      const res = await axiosInstance.post(
        `${baseUrl}/device/upload-meter-photo/${target}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${Token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const updatedDevice = res?.data?.data?.device;
      const newLat = latitude !== undefined && latitude !== null ? Number(latitude) : deviceDetails.latitude || 28.541505;
      const newLng = longitude !== undefined && longitude !== null ? Number(longitude) : deviceDetails.longitude || 77.332897;
      const newMeterPhoto = updatedDevice?.meterPhoto || (photoFile ? { fileName: photoFile.name, uploadedAt: new Date() } : deviceDetails.meterPhoto);

      if (target === selectedMeter) {
        setDeviceDetails((prev) => ({
          ...prev,
          latitude: newLat,
          longitude: newLng,
          lat: newLat,
          lng: newLng,
          meterPhoto: newMeterPhoto,
        }));
      }

      setMeters((prevMeters) =>
        prevMeters.map((m) =>
          (m.value || m.productId) === target
            ? {
                ...m,
                latitude: newLat,
                longitude: newLng,
                meterPhoto: newMeterPhoto,
              }
            : m
        )
      );

      toast({
        title: `Meter photo & coordinates updated for ${target}`,
        status: 'success',
      });

      fetchDeviceSpecs();
      fetchMeters();
    } catch (err) {
      const errMsg = err?.response?.data?.message || 'Failed to update meter photo & coordinates';
      toast({ title: errMsg, status: 'error' });
    }
  };

  // 5. Limits (Internal, Compliance & Alert) Handlers
  const handleOpenLimitModal = (type, meterId) => {
    const target =
      typeof meterId === 'string' && meterId
        ? meterId
        : selectedMeter !== 'All'
        ? selectedMeter
        : meters[0]?.value || '';

    const matchedMeter = meters.find(
      (m) => (m.value || m.productId) === target
    );
    const localLimits = JSON.parse(
      localStorage.getItem(`limits_${target}`) || '{}'
    );

    let currentVal = 0;
    let nocData = {};
    let currentAlertLimit = 0;
    let currentAlertEmail = '';
    let currentAlertFrequency = 'daily';

    if (type === 'alert') {
      const devObj = (target === selectedMeter ? deviceDetails : null) || matchedMeter || {};
      currentAlertLimit =
        devObj.alertLimit !== undefined && devObj.alertLimit !== null
          ? Number(devObj.alertLimit)
          : localLimits.alertLimit !== undefined && localLimits.alertLimit !== null
          ? Number(localLimits.alertLimit)
          : 0;
      currentAlertEmail = devObj.alertEmail || localLimits.alertEmail || '';
      currentAlertFrequency = devObj.alertFrequency || localLimits.alertFrequency || 'daily';
      currentVal = currentAlertLimit;
    } else if (type === 'compliance') {
      if (target === selectedMeter && deviceDetails?.nocDetails) {
        nocData = deviceDetails.nocDetails;
      } else if (matchedMeter?.nocDetails) {
        nocData = matchedMeter.nocDetails;
      } else if (localLimits?.nocDetails) {
        nocData = localLimits.nocDetails;
      }

      if (matchedMeter?.complianceLimit !== undefined && matchedMeter?.complianceLimit !== null) {
        currentVal = Number(matchedMeter.complianceLimit);
      } else if (target === selectedMeter && complianceLimit !== undefined) {
        currentVal = complianceLimit;
      } else if (localLimits.complianceLimit !== undefined) {
        currentVal = Number(localLimits.complianceLimit);
      }
    } else {
      if (matchedMeter?.internalLimit !== undefined && matchedMeter?.internalLimit !== null) {
        currentVal = Number(matchedMeter.internalLimit);
      } else if (target === selectedMeter && internalLimit !== undefined) {
        currentVal = internalLimit;
      } else if (localLimits.internalLimit !== undefined) {
        currentVal = Number(localLimits.internalLimit);
      }
    }

    setLimitModalState({
      isOpen: true,
      type,
      targetMeter: target,
      existingLimit: currentVal || 0,
      existingNocDetails: nocData || {},
      existingAlertLimit: currentAlertLimit || 0,
      existingAlertEmail: currentAlertEmail || '',
      existingAlertFrequency: currentAlertFrequency || 'daily',
    });
  };

  const handleSaveLimit = async (arg1, arg2, arg3, arg4) => {
    let targetMeter, newLimit, limitType, complianceData;
    if (typeof arg1 === 'object' && arg1 !== null) {
      targetMeter = arg1.meterId || selectedMeter;
      newLimit = Number(arg1.dailyLimit || arg1.alertLimit) || 0;
      limitType = arg1.type || 'compliance';
      complianceData = arg1;
    } else {
      targetMeter = arg1;
      newLimit = Number(arg2) || 0;
      limitType = arg3 || 'compliance';
      complianceData = arg4 || null;
    }

    try {
      const matchedDevice =
        (deviceDetails.productId === targetMeter ? deviceDetails : null) ||
        meters.find((m) => (m.value || m.productId) === targetMeter);
      const devId = matchedDevice?._id || matchedDevice?.id || targetMeter;

      if (limitType === 'alert') {
        const alertLimitVal = Number(complianceData?.alertLimit) || newLimit;
        const alertEmailVal = complianceData?.alertEmail || '';
        const alertFreqVal = complianceData?.alertFrequency || 'daily';

        toast({
          title: `Saving alert limit (${alertLimitVal.toFixed(2)} ${unit} / ${alertFreqVal}) for ${targetMeter}...`,
          status: 'info',
        });

        const payload = {
          alertLimit: alertLimitVal,
          alertEmail: alertEmailVal,
          alertFrequency: alertFreqVal,
        };

        if (targetMeter === selectedMeter) {
          setDeviceDetails((prev) => ({
            ...prev,
            alertLimit: alertLimitVal,
            alertEmail: alertEmailVal,
            alertFrequency: alertFreqVal,
          }));
        }

        // Persist in localStorage per meter for instant reload
        const savedKey = `limits_${targetMeter}`;
        const prevSaved = JSON.parse(localStorage.getItem(savedKey) || '{}');
        const updatedSaved = {
          ...prevSaved,
          alertLimit: alertLimitVal,
          alertEmail: alertEmailVal,
          alertFrequency: alertFreqVal,
        };
        localStorage.setItem(savedKey, JSON.stringify(updatedSaved));

        // Update meters array in state
        setMeters((prevMeters) =>
          prevMeters.map((m) =>
            (m.value || m.productId) === targetMeter
              ? {
                  ...m,
                  alertLimit: alertLimitVal,
                  alertEmail: alertEmailVal,
                  alertFrequency: alertFreqVal,
                }
              : m
          )
        );

        // Attempt backend update
        await axiosInstance
          .put(`${baseUrl}/device/update/${devId}`, payload, {
            headers: { Authorization: `Bearer ${Token}` },
          })
          .catch(() => null);

        toast({
          title: `Alert limit set to ${alertLimitVal.toFixed(2)} ${unit} (${alertFreqVal}) for ${alertEmailVal || 'device'}`,
          status: 'success',
        });
      } else if (limitType === 'compliance' && complianceData) {
        toast({
          title: `Saving compliance limit & NOC details for ${targetMeter}...`,
          status: 'info',
        });

        const formData = new FormData();
        if (complianceData.dailyLimit !== undefined) formData.append('dailyLimit', complianceData.dailyLimit);
        if (complianceData.yearlyLimit !== undefined) formData.append('yearlyLimit', complianceData.yearlyLimit);
        if (complianceData.nocNumber) formData.append('nocNumber', complianceData.nocNumber);
        if (complianceData.startDate) formData.append('startDate', complianceData.startDate);
        if (complianceData.endDate) formData.append('endDate', complianceData.endDate);
        if (complianceData.nocFile) formData.append('noc', complianceData.nocFile);

        const uploadRes = await axiosInstance.post(
          `${baseUrl}/device/upload-noc/${targetMeter}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${Token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        const updatedDevice = uploadRes?.data?.data;
        const newComplianceLimit = Number(complianceData.dailyLimit) || newLimit;

        if (targetMeter === selectedMeter) {
          setComplianceLimit(newComplianceLimit);
          if (updatedDevice?.nocDetails) {
            setDeviceDetails((prev) => ({
              ...prev,
              complianceLimit: newComplianceLimit,
              nocDetails: updatedDevice.nocDetails,
            }));
          }
        }

        // Persist in localStorage per meter for instant reload
        const savedKey = `limits_${targetMeter}`;
        const prevSaved = JSON.parse(localStorage.getItem(savedKey) || '{}');
        const updatedSaved = {
          ...prevSaved,
          complianceLimit: newComplianceLimit,
          nocDetails: updatedDevice?.nocDetails || {
            nocNumber: complianceData.nocNumber,
            dailyLimit: complianceData.dailyLimit,
            yearlyLimit: complianceData.yearlyLimit,
            startDate: complianceData.startDate,
            endDate: complianceData.endDate,
            fileName: complianceData.nocFile?.name || prevSaved?.nocDetails?.fileName,
          },
        };
        localStorage.setItem(savedKey, JSON.stringify(updatedSaved));

        setMeters((prevMeters) =>
          prevMeters.map((m) =>
            (m.value || m.productId) === targetMeter
              ? {
                  ...m,
                  complianceLimit: newComplianceLimit,
                  nocDetails: updatedSaved.nocDetails,
                }
              : m
          )
        );

        toast({
          title: `Compliance limit updated to ${newComplianceLimit.toFixed(2)} ${unit} & NOC saved`,
          status: 'success',
        });
      } else {
        toast({
          title: `Saving ${limitType} limit (${newLimit.toFixed(2)} ${unit}) for ${targetMeter}...`,
          status: 'info',
        });

        const payload = {};
        if (limitType === 'compliance') {
          payload.complianceLimit = newLimit;
          if (targetMeter === selectedMeter) {
            setComplianceLimit(newLimit);
          }
        } else {
          payload.internalLimit = newLimit;
          if (targetMeter === selectedMeter) {
            setInternalLimit(newLimit);
          }
        }

        // Persist in localStorage per meter for instant reload
        const savedKey = `limits_${targetMeter}`;
        const prevSaved = JSON.parse(localStorage.getItem(savedKey) || '{}');
        const updatedSaved = {
          ...prevSaved,
          [limitType === 'compliance' ? 'complianceLimit' : 'internalLimit']: newLimit,
        };
        localStorage.setItem(savedKey, JSON.stringify(updatedSaved));

        // Update meters array in state so All Meters table and views update immediately
        setMeters((prevMeters) =>
          prevMeters.map((m) =>
            (m.value || m.productId) === targetMeter
              ? {
                  ...m,
                  [limitType === 'compliance' ? 'complianceLimit' : 'internalLimit']: newLimit,
                }
              : m
          )
        );

        // Attempt backend update
        await axiosInstance
          .put(`${baseUrl}/device/update/${devId}`, payload, {
            headers: { Authorization: `Bearer ${Token}` },
          })
          .catch(() => null);

        toast({
          title: `${limitType === 'compliance' ? 'Compliance' : 'Internal'} limit updated to ${newLimit.toFixed(2)} ${unit}`,
          status: 'success',
        });
      }

      if (targetMeter === selectedMeter) {
        fetchDeviceSpecs();
      }
      fetchMeters();
    } catch (err) {
      const errMsg = err?.response?.data?.message || 'Failed to update limit';
      toast({ title: errMsg, status: 'error' });
    }
  };

  return (
    <div className="client-app">
      {/* Interactive Limit Editor Modal */}
      <LimitModal
        isOpen={limitModalState.isOpen}
        onClose={() => setLimitModalState((prev) => ({ ...prev, isOpen: false }))}
        type={limitModalState.type}
        meters={meters}
        selectedMeter={limitModalState.targetMeter || selectedMeter}
        existingLimit={limitModalState.existingLimit}
        existingNocDetails={limitModalState.existingNocDetails}
        existingAlertLimit={limitModalState.existingAlertLimit}
        existingAlertEmail={limitModalState.existingAlertEmail}
        existingAlertFrequency={limitModalState.existingAlertFrequency}
        unit={unit}
        onSave={handleSaveLimit}
      />

      {/* Interactive Meter Photo & GPS Coordinates Modal */}
      <MeterPhotoModal
        isOpen={meterPhotoModalState.isOpen}
        onClose={() => setMeterPhotoModalState((prev) => ({ ...prev, isOpen: false }))}
        meters={meters}
        selectedMeter={meterPhotoModalState.targetMeter || selectedMeter}
        deviceDetails={deviceDetails}
        baseUrl={baseUrl}
        Token={Token}
        onSave={handleSaveMeterPhoto}
      />

      {/* 1. Header */}
      <ClientNavbar
        currentView={currentView}
        setCurrentView={setCurrentView}
        isLoading={loading}
        onRefresh={() => {
          fetchTelemetry();
          fetchHeroGraph();
          fetchDailyConsumptionData();
          fetchMotorAnalytics();
          toast({ title: 'Telemetry refreshed', status: 'success', duration: 2000 });
        }}
      />

      <main className="client-content">
        {currentView === 'profile' ? (
          <ClientProfileView />
        ) : currentView === 'users' ? (
          <section className="client-users-view">
            <UsersView />
          </section>
        ) : (
          <section className="client-dashboard-view">
            {/* 2. Top Filter Bar */}
            <TopFilterBar
              plants={plants}
              selectedPlant={selectedPlant}
              onPlantChange={(p) => {
                setSelectedPlant(p);
                const match = plants.find((x) => x.value === p);
                if (match) {
                  setPlantName(match.label);
                }
              }}
              meters={meters}
              selectedMeter={selectedMeter}
              onMeterChange={(m) => setSelectedMeter(m)}
              unit={unit}
              onUnitChange={(u) => {
                setUnit(u);
                toast({ title: `Units switched to ${u}`, status: 'info', duration: 2000 });
              }}
              hasMeterPhoto={Boolean(
                selectedMeter && selectedMeter !== 'All'
                  ? deviceDetails?.meterPhoto?.fileName ||
                    deviceDetails?.meterPhoto?.storedFileName ||
                    meters.find((m) => (m.value || m.productId) === selectedMeter)?.meterPhoto?.fileName ||
                    meters.find((m) => (m.value || m.productId) === selectedMeter)?.meterPhoto?.storedFileName
                  : meters.some((m) => m.meterPhoto?.fileName || m.meterPhoto?.storedFileName)
              )}
              onMeterPhotoClick={() => handleOpenMeterPhotoModal()}
            />

            {/* 3. Dual-Mode View: Single Meter vs All Meters */}
            {selectedMeter === 'All' ? (
              <AllMetersView
                plantName={plantName}
                meters={meters}
                unit={unit}
                todayTotal={todayConsumption}
                onSelectMeter={(mId) => setSelectedMeter(mId)}
                onExport={() => handleDownloadGraph('excel')}
                onDownloadTable={(tbl, format) => handleDownloadGraph(format)}
                onViewCert={handleDownloadCert}
                onSetComplianceLimit={(mId) => handleOpenLimitModal('compliance', mId)}
                onSetInternalLimit={(mId) => handleOpenLimitModal('internal', mId)}
                onOpenMeterPhoto={() => handleOpenMeterPhotoModal()}
              />
            ) : (
              <div id="singleMeter">
                {/* Single Meter Header */}
                <div className="page-h">
                  <div>
                    <h1>Dashboard</h1>
                    <p>
                      {selectedMeter || 'Select Meter'} ·{' '}
                      {plantName} · last communicated {deviceDetails.lastCommunicated || 'Recently'}
                    </p>
                  </div>
                  <div className="btnrow" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                      className="btn"
                      onClick={() => handleOpenLimitModal('internal')}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                      </svg>
                      Set internal limit
                    </button>
                    <button
                      className="btn primary"
                      onClick={() => handleOpenLimitModal('compliance')}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4M12 3l7 4v5c0 5-3.5 7.5-7 9-3.5-1.5-7-4-7-9V7z" />
                      </svg>
                      Set compliance limit
                    </button>
                    <button
                      className="btn"
                      onClick={() => handleOpenLimitModal('alert')}
                      title="Configure daily, weekly, or monthly consumption alert limit"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                      </svg>
                      Set alert limit
                    </button>
                  </div>
                </div>

                {/* 4. KPI Strip */}
                <KpiStrip
                  todayConsumption={todayConsumption}
                  todayDelta={todayDelta}
                  monthConsumption={monthConsumption}
                  monthDelta={null}
                  cumulativePumpHours={cumulativePumpHours}
                  todayPumpHours={todayPumpHours}
                  flowDuration={flowDuration}
                  totalizer={totalizer}
                  forwardTotalizer={forwardTotalizer}
                  reverseTotalizer={reverseTotalizer}
                  borewellId={deviceDetails.cgwaBorewellId || ''}
                  unit={unit}
                />

                {/* 4b. Statutory & Calibration Compliance Strip */}
                <ComplianceStrip
                  deviceDetails={deviceDetails}
                  unit={unit}
                  selectedMeter={selectedMeter}
                  onViewNoc={handleDownloadNoc}
                  onViewCert={handleDownloadCert}
                />

                {/* 5. Hero Gauge & Bar Chart */}
                <HeroGaugeChart
                  todayExtraction={todayConsumption}
                  internalLimit={internalLimit}
                  complianceLimit={complianceLimit}
                  unit={unit}
                  fromDate={heroFromDate}
                  toDate={heroToDate}
                  onFromDateChange={setHeroFromDate}
                  onToDateChange={setHeroToDate}
                  aggregation={heroAggregation}
                  onAggregationChange={handleHeroAggregationChange}
                  onExport={() => handleDownloadHeroGraph('excel')}
                  seriesData={heroGraphSeries}
                />

                {/* 6. Sticky Segmented Tab Navigation */}
                <ClientTabNav activeTab={activeTab} onTabChange={setActiveTab} />

                {/* 7. Tab Panes */}
                {activeTab === 'overview' && (
                  <OverviewTab
                    deviceData={deviceDetails}
                    internalLimit={internalLimit}
                    complianceLimit={complianceLimit}
                    unit={unit}
                    maxExtraction={maxDailyExtraction}
                    avgExtraction={avgDailyExtraction}
                    reverseFlow={0.0}
                    breachesCount={calculatedBreachesCount}
                    fromDate={fromDate}
                    toDate={toDate}
                    onFromDateChange={setFromDate}
                    onToDateChange={setToDate}
                    onExport={() => handleDownloadGraph('excel')}
                    onViewCert={handleDownloadCert}
                    onOpenMeterPhoto={() => handleOpenMeterPhotoModal()}
                  />
                )}

                {activeTab === 'consumption' && (
                  <ConsumptionTab
                    unit={unit}
                    fromDate={fromDate}
                    toDate={toDate}
                    onFromDateChange={setFromDate}
                    onToDateChange={setToDate}
                    onExport={() => handleDownloadGraph('excel', '1d', 'daily-consumption')}
                    dailyLogs={dailyConsumptionLogs}
                    onDownloadTable={(name, fmt, customInterval) =>
                      handleDownloadGraph(
                        fmt === 'pdf' ? 'pdf' : 'excel',
                        customInterval || (name === 'daily-consumption' ? '1d' : null),
                        name
                      )
                    }
                    internalLimit={internalLimit}
                    complianceLimit={complianceLimit}
                    pumpRecords={pumpRecordsList}
                    selectedMeter={selectedMeter}
                    baseUrl={baseUrl}
                    Token={Token}
                    axiosInstance={axiosInstance}
                  />
                )}

                {activeTab === 'pump' && (
                  <PumpTab
                    fromDate={fromDate}
                    toDate={toDate}
                    onFromDateChange={setFromDate}
                    onToDateChange={setToDate}
                    onExport={() => handleDownloadPump('excel')}
                    pumpStats={pumpMetrics}
                    pumpRecords={pumpRecordsList}
                    onDownloadPump={(fmt) => handleDownloadPump(fmt === 'pdf' ? 'pdf' : 'excel')}
                  />
                )}

                {activeTab === 'reports' && (
                  <ReportsTab
                    selectedMeter={selectedMeter || deviceDetails.productId || (meters.length > 0 ? meters[0].value : '')}
                    meters={meters}
                    baseUrl={baseUrl}
                    Token={Token}
                    axiosInstance={axiosInstance}
                    deviceDetails={deviceDetails}
                    onDownloadCert={handleDownloadCert}
                    onDownloadNoc={handleDownloadNoc}
                    onDownloadRenewal={() => handleDownloadGraph('excel')}
                    onDownloadMonthlyArchive={(startDate, endDate, format = 'excel') =>
                      handleDownloadGraph(format, '1d', 'daily-consumption')
                    }
                  />
                )}

                {activeTab === 'subscription' && (
                  <SubscriptionTab
                    deviceData={deviceDetails}
                  />
                )}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
